/**
 * Handing the finished film on: the phone's own share sheet, a download, or the
 * link to the memorial page. On computers the link is what's shared: it brings people
 * to the page, with the slideshow playing.
 */

export type ShareOutcome = "shared" | "cancelled" | "needs-tap" | "unsupported";

const UNSAFE_FILE_CHARS = /[\s"'`<>:/\\|?*.,;!()[\]{}]+/g;
const EDGE_DASHES = /^-+|-+$/g;

export function videoFile(blob: Blob, name: string, extension: string): File {
  return new File([blob], `${name}.${extension}`, { type: blob.type });
}

function canShare(data: ShareData): boolean {
  try {
    return !!navigator.canShare?.(data);
  } catch {
    return false;
  }
}

function canShareFile(file: File): boolean {
  return canShare({ files: [file] });
}

/** Whether this device's share sheet takes video files at all (phones, tablets, Macs, Windows). */
export function canShareFiles(): boolean {
  return canShareFile(new File([""], "film.mp4", { type: "video/mp4" }));
}

function outcomeOf(e: unknown): ShareOutcome {
  const name = (e as DOMException)?.name;
  if (name === "AbortError") {
    return "cancelled";
  }
  return name === "NotAllowedError" ? "needs-tap" : "unsupported";
}

/**
 * Open the share sheet with the film. `text` (the memorial page link) goes along as the
 * message where the chosen app takes one, like WhatsApp, Messenger and SMS; apps that
 * don't (Instagram) ignore it. When the device can't take file + text together, the film
 * goes alone. Phones allow this only shortly after a tap; when that has passed the
 * browser rejects with NotAllowedError and the caller asks for one more tap.
 */
export async function shareFile(
  file: File,
  text?: string
): Promise<ShareOutcome> {
  if (!canShareFile(file)) {
    return "unsupported";
  }
  const data: ShareData =
    text && canShare({ files: [file], text })
      ? { files: [file], text }
      : { files: [file] };
  try {
    await navigator.share(data);
    return "shared";
  } catch (e) {
    return outcomeOf(e);
  }
}

export async function shareLink(
  url: string,
  text: string
): Promise<ShareOutcome> {
  if (!navigator.share) {
    return "unsupported";
  }
  try {
    await navigator.share({ text, url });
    return "shared";
  } catch (e) {
    return outcomeOf(e) === "cancelled" ? "cancelled" : "unsupported";
  }
}

export function download(file: File) {
  const url = URL.createObjectURL(file);
  const a = document.createElement("a");
  a.href = url;
  a.download = file.name;
  a.rel = "noopener";
  document.body.appendChild(a);
  a.click();
  a.remove();
  setTimeout(() => URL.revokeObjectURL(url), 60_000);
}

/** Share dialogs that take a link (computers, and devices that can't make the film). */
/** Marks a shared link, so the visits it brings show in GA4 as source "bildspel". */
const SHARE_SOURCE = "bildspel";
/** The life story's links and QR codes: `utm_content` (SlideshowQrRedirect::LIFE_STORY). */
export const LIFE_STORY_CONTENT = "tidslinje";

/**
 * The page's address as shared through one channel (qr, film, link, copy, facebook, …):
 * GA4 reads utm_source/utm_medium by itself, and WordPress keeps them through its redirects.
 */
export function taggedUrl(
  url: string,
  medium: string,
  content?: string
): string {
  try {
    const u = new URL(url);
    u.searchParams.set("utm_source", SHARE_SOURCE);
    u.searchParams.set("utm_medium", medium);
    if (content) {
      u.searchParams.set("utm_content", content);
    }
    // The section's own share image and title (PRO-4397): a #hash never reaches the crawler.
    u.searchParams.set(
      "del",
      content === LIFE_STORY_CONTENT ? "tidslinje" : "bildspel"
    );
    return u.toString();
  } catch {
    return url;
  }
}

/** Arrived through a shared slideshow link or QR code: "shared_film", "shared_qr", …; else null. */
export function arrivalSource(): string | null {
  const q = new URLSearchParams(window.location.search);
  return q.get("utm_source") === SHARE_SOURCE
    ? `shared_${q.get("utm_medium") ?? "link"}`
    : null;
}

export const LINK_SHARE_URLS = {
  facebook: (u: string) =>
    `https://www.facebook.com/sharer/sharer.php?u=${encodeURIComponent(u)}`,
  linkedin: (u: string) =>
    `https://www.linkedin.com/sharing/share-offsite/?url=${encodeURIComponent(u)}`,
  whatsapp: (text: string) => `https://wa.me/?text=${encodeURIComponent(text)}`,
};

/**
 * Facebook's SDK, for Messenger's send dialog on computers. The page header already sets
 * `fbAsyncInit` with Lavendla's app id (as the "Dela sida" box uses it); this only loads it.
 */
export function loadFacebookSdk(lang: string) {
  if (document.getElementById("facebook-jssdk")) {
    return;
  }
  const locale = lang.toLowerCase().startsWith("da") ? "da_DK" : "sv_SE";
  const js = document.createElement("script");
  js.id = "facebook-jssdk";
  js.async = true;
  js.src = `https://connect.facebook.net/${locale}/sdk.js`;
  document.head.appendChild(js);
}

/** "Birgitta-Lindqvist-minnesfilm" */
export function fileBaseName(name: string, suffix: string): string {
  const safe = name
    .normalize("NFC")
    .replace(UNSAFE_FILE_CHARS, "-")
    .replace(EDGE_DASHES, "");
  const base = safe || "bildspel";
  return suffix ? `${base}-${suffix}` : base;
}

/**
 * A page's short address for its QR code: WordPress's `?p=<id>` link, which redirects to
 * the page and keeps the `#part`. About 40 characters instead of 65, so the code has a
 * smaller grid with bigger dots, and it scans from further away. The full address when the
 * page's id isn't known.
 *
 * `fromQr` marks the code guests scan: a short `&qr` the redirect turns into the GA4 tags
 * (SlideshowQrRedirect.php), rather than tags that would double the address.
 */
export function qrAddress(
  pageUrl: string,
  pageId: string,
  hash = "",
  fromQr = false,
  content = ""
): string {
  const fragment = hash ? `#${hash}` : "";
  try {
    if (Number(pageId) > 0) {
      // `&qr=tidslinje` adds the life story's tag on the way (SlideshowQrRedirect.php).
      const mark = fromQr ? `&qr${content ? `=${content}` : ""}` : "";
      return `${new URL(pageUrl).origin}/?p=${pageId}${mark}${fragment}`;
    }
  } catch {
    // Not a full address: use it as it is.
  }
  const full = `${pageUrl.split("#")[0]}${fragment}`;
  return fromQr ? taggedUrl(full, "qr", content || undefined) : full;
}

/** The QR code's purple: $color__main-purple, dark enough against white to scan like black. */
const QR_COLOR = "#480640";
/** Quiet zone, in modules; the white card around it adds a little more. */
const QR_MARGIN = 2;
/** Dot diameter as a share of a module: rounder looks softer, smaller scans worse. */
const QR_DOT = 0.95;
/** The three corner markers ("eyes") are 7 modules square. */
const QR_EYE = 7;

const qrNum = (n: number) => Number(n.toFixed(3));
const qrRoundRect = (x: number, y: number, size: number, r: number) =>
  `M${x + r} ${y}h${qrNum(size - 2 * r)}a${r} ${r} 0 0 1 ${r} ${r}v${qrNum(size - 2 * r)}` +
  `a${r} ${r} 0 0 1 ${-r} ${r}h${qrNum(2 * r - size)}a${r} ${r} 0 0 1 ${-r} ${-r}` +
  `v${qrNum(2 * r - size)}a${r} ${r} 0 0 1 ${r} ${-r}z`;

/**
 * An inline SVG QR code in Lavendla's purple: round dots and rounded corner markers,
 * error correction M, no logo inside (a logo needs level H, which made the code too
 * dense to scan at the share card's size or across a room). Checked with three decoders
 * at 72–180px and on a washed-out projector (scratch research, PRO-4209). The library
 * loads only when a QR code is shown.
 */
export async function qrSvg(url: string): Promise<string> {
  const { default: qrcode } = await import("qrcode-generator");
  const qr = qrcode(0, "M");
  qr.addData(url);
  qr.make();
  const n = qr.getModuleCount();
  const size = n + QR_MARGIN * 2;
  const inEye = (r: number, c: number) =>
    (r < QR_EYE && c < QR_EYE) ||
    (r < QR_EYE && c >= n - QR_EYE) ||
    (r >= n - QR_EYE && c < QR_EYE);

  const radius = QR_DOT / 2;
  let dots = "";
  for (let r = 0; r < n; r += 1) {
    for (let c = 0; c < n; c += 1) {
      if (inEye(r, c) || !qr.isDark(r, c)) {
        continue;
      }
      const x = c + QR_MARGIN + 0.5 - radius;
      const y = r + QR_MARGIN + 0.5;
      dots += `M${qrNum(x)} ${y}a${radius} ${radius} 0 1 0 ${QR_DOT} 0a${radius} ${radius} 0 1 0 ${-QR_DOT} 0z`;
    }
  }

  let eyes = "";
  for (const [r, c] of [
    [0, 0],
    [0, n - QR_EYE],
    [n - QR_EYE, 0],
  ]) {
    const x = c + QR_MARGIN;
    const y = r + QR_MARGIN;
    // A ring (outer minus inner rounded square) and a rounded centre.
    eyes +=
      qrRoundRect(x, y, QR_EYE, 2.2) +
      qrRoundRect(x + 1, y + 1, QR_EYE - 2, 1.3) +
      qrRoundRect(x + 2, y + 2, QR_EYE - 4, 0.9);
  }

  return (
    `<svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 ${size} ${size}" aria-hidden="true">` +
    `<path fill="${QR_COLOR}" d="${dots}"/>` +
    `<path fill="${QR_COLOR}" fill-rule="evenodd" d="${eyes}"/>` +
    "</svg>"
  );
}
